import { motion } from "framer-motion";
import { Logo } from "../brand/Logo";
import { Eyebrow } from "../motion/Reveal";
import { useSite } from "../../lib/site";

const EASE = [0.2, 0.8, 0.2, 1];
const ALTS = [
  "Personas latinas aprendiendo a generar clientes en una capacitación",
  "Distribuidor presentando una solución digital a la dueña de una tienda de barrio",
  "Emprendedora trabajando con herramientas digitales en su laptop y celular",
];

const Point = ({ neg, pos, image, index }) => {
  const flip = index % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1, ease: EASE }}
      className="grid items-center gap-8 border-t border-white/10 py-12 first:border-t-0 first:pt-0 lg:grid-cols-12 lg:gap-14 lg:py-16"
      data-testid={`manifiesto-point-${index}`}
    >
      <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <div className={`relative aspect-[4/3] overflow-hidden border border-white/10 ${flip ? "rounded-[2rem_6rem_2rem_2rem]" : "rounded-[6rem_2rem_2rem_2rem]"}`}>
          {image && (
            <motion.img
              src={image}
              alt={ALTS[index] || ""}
              initial={{ scale: 1.12 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1.6, ease: EASE }}
              className="h-full w-full object-cover"
              data-testid={`manifiesto-image-${index}`}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
          <span className="absolute bottom-4 left-4 rounded-full bg-ink/80 px-3 py-1.5 font-mono text-xs text-leaf backdrop-blur">0{index + 1}</span>
        </div>
      </div>
      <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
        <p className="font-display text-xl font-semibold uppercase leading-tight tracking-[-0.02em] text-white/70 sm:text-2xl lg:text-[1.75rem]" data-testid={`manifiesto-line-${index * 2}`}>
          {neg}
        </p>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="mt-4 font-display text-2xl font-semibold uppercase leading-tight tracking-[-0.02em] text-leaf sm:text-3xl lg:text-[2.25rem]"
          data-testid={`manifiesto-line-${index * 2 + 1}`}
        >
          {pos}
        </motion.p>
      </div>
    </motion.article>
  );
};

export const Manifesto = () => {
  const { content } = useSite();
  const m = content.manifiesto;
  const images = content.media.manifiesto_images || [];
  const pairs = [];
  for (let i = 0; i < m.lines.length; i += 2) pairs.push([m.lines[i], m.lines[i + 1] || ""]);

  return (
    <section className="grain relative overflow-hidden bg-ink py-24 text-white sm:py-32 lg:py-40" data-testid="manifiesto-section">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-leaf/10 blur-[180px]" />
      <div className="container-x relative">
        <Eyebrow>{m.eyebrow}</Eyebrow>
        <div className="mt-12">
          {pairs.map(([neg, pos], i) => (
            <Point key={i} neg={neg} pos={pos} image={images[i]} index={i} />
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1 }}
          className="mt-6 flex flex-col items-start gap-6 border-t border-white/15 pt-12 sm:flex-row sm:items-center sm:justify-between"
          data-testid="manifiesto-closing"
        >
          <Logo tone="light" className="h-12 sm:h-16" testId="manifiesto-logo" />
          <p className="font-display text-2xl font-medium tracking-tight text-white sm:text-3xl">{m.closing_text}</p>
        </motion.div>
      </div>
    </section>
  );
};
